import React, { useState, useEffect, useCallback, useRef } from "react";
import Layout from "../Components/Layout";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  useMediaQuery,
  useTheme,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Button,
  Chip,
  Autocomplete,
} from "@mui/material";
import {
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getAllConversationListByAdmin } from "../redux/actions/conversationsActions";
import { getUsersListForDropdown } from "../redux/actions/userActions";
import useCallOnce from "../hooks/useCallOnce";
import { getWebhookData } from "../redux/actions/billingActions";
import ConversationDetailSidebar from "./Conversations/components/ConversationDetailSidebar";

function AllConversations() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { webhookData } = useSelector((state) => state.billing);
  const webhookPerCredit = webhookData?.getCosting[0]?.percredit || 0;
  const { conversations, loading, pagination, summary } = useSelector(
    (state) => state.adminConversations
  );
  const { dropdown } = useSelector((state) => state.users);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    call_summary_title: "",
    call_successful: "",
    convoId: "",
    userId: "",
    type: "",
    searchKeyword: "",
    customerEmail: "",
  });

  // Brand filter state
  const [userInput, setUserInput] = useState("");
  const [selectedUserOption, setSelectedUserOption] = useState(null);
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Sidebar state
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Track initial load to prevent multiple calls
  const initialLoadCalled = useRef(false);

  // Load users dropdown on mount
  useCallOnce(
    "all-conversations/users/dropdown",
    () => {
      dispatch(getUsersListForDropdown("", 1, 20));
    },
    [dispatch]
  );

  useCallOnce("billing/getWebhookData", () => {
    dispatch(getWebhookData());
  });

  // Debounced search for user dropdown
  const debouncedSearch = useCallback(
    (query) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      const timer = setTimeout(() => {
        if (query.length >= 2) {
          dispatch(getUsersListForDropdown(query, 1, 20));
        }
      }, 300);

      setDebounceTimer(timer);
    },
    [debounceTimer, dispatch]
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  // Fetch initial data
  useEffect(() => {
    if (!initialLoadCalled.current && !loading) {
      initialLoadCalled.current = true;
      const apiPage = page + 1;
      dispatch(getAllConversationListByAdmin(apiPage, rowsPerPage, filters));
    }
  }, [dispatch, rowsPerPage, loading]);

  // Helpers to render date as MM/DD/YYYY in TextField while storing ISO (YYYY-MM-DD)
  const formatDateForUi = (isoDateString) => {
    if (!isoDateString) return "";
    const [year, month, day] = isoDateString.split("-");
    if (!year || !month || !day) return "";
    return `${month}/${day}/${year}`;
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";

    let date;
    if (typeof dateValue === "string") {
      date = new Date(dateValue);
    } else {
      date = new Date(dateValue * 1000);
    }

    // Always present in US Eastern time (shows EST/EDT automatically)
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "America/New_York",
    }).format(date);
  };

  // UI Date Field
  const UiDateField = ({ label, value, onChange, min, max }) => {
    const hiddenDateRef = React.useRef(null);

    const openPicker = () => {
      const el = hiddenDateRef.current;
      if (!el) return;
      try {
        el.focus();
      } catch (_) {}
      if (typeof el.showPicker === "function") {
        try {
          el.showPicker();
        } catch (_) {}
      } else {
        try {
          el.click();
        } catch (_) {}
      }
    };

    return (
      <Box
        sx={{ position: "relative" }}
        onMouseDown={(e) => {
          e.preventDefault();
          openPicker();
        }}
      >
        <TextField
          fullWidth
          label={label}
          type="text"
          placeholder="MM/DD/YYYY"
          value={formatDateForUi(value || "")}
          onFocus={openPicker}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem" } }}
          inputProps={{ readOnly: true }}
        />

        <input
          ref={hiddenDateRef}
          type="date"
          value={value || ""}
          min={min}
          max={max}
          onChange={(e) => onChange(e.target.value || "")}
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0,
            width: "100%",
            height: "100%",
            cursor: "pointer",
            zIndex: 10,
            pointerEvents: "auto",
          }}
        />
      </Box>
    );
  };

  const renderDate = (value) => {
    if (!value) return "-";
    const date =
      typeof value === "number" ? new Date(value * 1000) : new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "America/New_York",
    }).format(date);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    }
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const formatCost = (cost) => {
    if (!cost) return "-";
    return `$${parseFloat(cost).toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "success";
      case "unknown":
        return "default";
      case "failure":
        return "error";
      default:
        return "default";
    }
  };
  //   switch (status?.toLowerCase()) {
  //     case "successful":
  //     case "completed":
  //       return "success";
  //     case "failed":
  //     case "error":
  //       return "error";
  //     case "pending":
  //     case "processing":
  //       return "warning";
  //     default:
  //       return "default";
  //   }
  // };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const apiPage = newPage + 1;
    const userId = selectedUserOption
      ? selectedUserOption._id ||
        selectedUserOption.userId ||
        selectedUserOption.id
      : "";
    const updatedFilters = { ...filters, userId };
    dispatch(
      getAllConversationListByAdmin(apiPage, rowsPerPage, updatedFilters)
    );
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    const userId = selectedUserOption
      ? selectedUserOption._id ||
        selectedUserOption.userId ||
        selectedUserOption.id
      : "";
    const updatedFilters = { ...filters, userId };
    dispatch(getAllConversationListByAdmin(1, newRowsPerPage, updatedFilters));
  };

  const handleFilter = () => {
    setPage(0);
    dispatch(getWebhookData());
    const userId = selectedUserOption
      ? selectedUserOption._id ||
        selectedUserOption.userId ||
        selectedUserOption.id
      : "";
    const updatedFilters = { ...filters, userId };
    dispatch(getAllConversationListByAdmin(1, rowsPerPage, updatedFilters));
  };

  const handleReset = () => {
    const reset = {
      fromDate: "",
      toDate: "",
      call_summary_title: "",
      call_successful: "",
      convoId: "",
      userId: "",
      type: "",
      searchKeyword: "",
      customerEmail: "",
    };
    setFilters(reset);
    setUserInput("");
    setSelectedUserOption(null);
    setPage(0);
    dispatch(getWebhookData());
    dispatch(getAllConversationListByAdmin(1, rowsPerPage, reset));
  };

  // Handle row click to open sidebar
  const handleRowClick = (conversationId) => {
    setSelectedConversationId(conversationId);
    setSidebarOpen(true);
  };

  // Handle sidebar close
  const handleSidebarClose = () => {
    setSidebarOpen(false);
    setSelectedConversationId(null);
  };

  return (
    <Layout>
      <Box sx={{ mt: 0, px: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "start",
            flexDirection: "column",
            alignItems: "start",
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            All Conversations
          </Typography>
          {/* <Typography variant="body2" sx={{ color: '#6b7280' }}>
            View your AI agent conversations
          </Typography> */}
        </Box>

        {/* Filters Row */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <UiDateField
                label="Start Date"
                value={filters.fromDate}
                onChange={(val) => setFilters((f) => ({ ...f, fromDate: val }))}
                max={filters.toDate || undefined}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <UiDateField
                label="End Date"
                value={filters.toDate}
                onChange={(val) => setFilters((f) => ({ ...f, toDate: val }))}
                min={filters.fromDate || undefined}
              />
            </Grid>

           
           
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Conversation ID"
                placeholder="Enter conversation ID"
                value={filters.convoId}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, convoId: e.target.value }))
                }
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Email"
                placeholder="Enter email"
                value={filters.customerEmail}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, customerEmail: e.target.value }))
                }
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Search Query"
                placeholder="Type to search"
                value={filters.searchKeyword}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, searchKeyword: e.target.value }))
                }
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem" } }}
              />
            </Grid>
            
            
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                size="small"
                fullWidth
                options={dropdown.users || []}
                loading={Boolean(dropdown?.loading)}
                loadingText="Loading..."
                value={selectedUserOption}
                getOptionLabel={(u) =>
                  u?.email ||
                  u?.name ||
                  u?.company ||
                  u?._id ||
                  u?.userId ||
                  u?.id ||
                  ""
                }
                isOptionEqualToValue={(a, b) =>
                  (a?._id || a?.userId || a?.id) ===
                  (b?._id || b?.userId || b?.id)
                }
                onChange={(e, value) => {
                  setSelectedUserOption(value || null);
                }}
                inputValue={userInput}
                onInputChange={(e, value, reason) => {
                  setUserInput(value);
                  // Only trigger API on user typing, not on option select/clear
                  if (reason === "input") {
                    debouncedSearch(value || "");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Brand"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {dropdown?.loading ? (
                            <CircularProgress
                              color="inherit"
                              size={16}
                              sx={{ mr: 1 }}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
             
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type}
                  label="Type"
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, type: e.target.value }))
                  }
                >
                  {/* <MenuItem value="">All</MenuItem> */}
                  <MenuItem value="call">Call</MenuItem>
                  <MenuItem value="text">Text</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.call_successful}
                  label="Status"
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      call_successful: e.target.value,
                    }))
                  }
                >
                  {/* <MenuItem value="">All</MenuItem> */}
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="unknown">Unknown</MenuItem>
                  <MenuItem value="failure">Failure</MenuItem>
                </Select>
              </FormControl>
            </Grid>
             <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Title"
                placeholder="Title"
                value={filters.call_summary_title}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    call_summary_title: e.target.value,
                  }))
                }
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexDirection: { xs: "column", md: "row" },
                  "& > *": {
                    flex: { xs: 1, md: "none" },
                  },
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleFilter}
                  startIcon={<FilterIcon />}
                  size="medium"
                  sx={{
                    bgcolor: "#111827",
                    "&:hover": { bgcolor: "#0b1220" },
                    textTransform: "none",
                  }}
                >
                  Filter
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  startIcon={<RefreshIcon />}
                  size="medium"
                  sx={{
                    borderColor: "#111827",
                    color: "#111827",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#0b1220",
                      backgroundColor: "#f9fafb",
                    },
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          <Table
            stickyHeader
            sx={{
              tableLayout: "fixed",
              minWidth: 1350,
              "& td, & th": {
                px: { xs: 1, sm: 2 },
                py: { xs: 0.75, sm: 1 },
              },
              "& td .MuiTypography-root": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 70,
                    maxWidth: 70,
                    width: 70,
                  }}
                >
                  S. No
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 120,
                    maxWidth: 120,
                    width: 120,
                  }}
                >
                  Conv. ID
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 180,
                    maxWidth: 180,
                    width: 180,
                  }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 150,
                    maxWidth: 150,
                    width: 150,
                  }}
                >
                  Company Name
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,

                    minWidth: 180,
                    maxWidth: 180,
                    width: 180,
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 100,
                    maxWidth: 100,
                    width: 100,
                  }}
                >
                  Duration
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 100,
                    maxWidth: 100,
                    width: 100,
                  }}
                >
                  Messages
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 100,
                    maxWidth: 100,
                    width: 100,
                  }}
                >
                  Type
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 100,
                    maxWidth: 100,
                    width: 100,
                  }}
                >
                  Credits (Call)
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 100,
                    maxWidth: 100,
                    width: 100,
                  }}
                >
                  Credits (LLM)
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 100,
                    maxWidth: 100,
                    width: 100,
                  }}
                >
                  Cost (Call)
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 100,
                    maxWidth: 100,
                    width: 100,
                  }}
                >
                  Cost (LLM)
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 100,
                    maxWidth: 100,
                    width: 100,
                  }}
                >
                  Total Cost
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 100,
                    maxWidth: 100,
                    width: 100,
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 150,
                    maxWidth: 150,
                    width: 150,
                  }}
                >
                  Title
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={13}
                    sx={{ textAlign: "center", py: 8, height: 300 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body1" sx={{ color: "#6b7280" }}>
                        Loading conversations...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : conversations.length > 0 ? (
                conversations.map((item, index) => (
                  <TableRow
                    key={item._id || item.conversationId || index}
                    hover
                    onClick={() => handleRowClick(item.conversation_id)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#f8fafc" },
                    }}
                  >
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "#374151", fontWeight: 500 }}
                      >
                        {item?.convoId || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {item?.customerEmail ? item?.customerEmail : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {item?.user_company || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "#374151", fontWeight: 500, width: 200 }}
                      >
                        {formatDate(item?.metadata?.start_time_unix_secs)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {formatDuration(
                          item.call_duration_secs ||
                            item.metadata?.call_duration_secs
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {item.msg || item.message_count || 0}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {item?.metadata?.phone_call === null ? "Text" : "Call"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {item?.metadata?.charging?.call_charge || 0}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {item?.metadata?.charging?.llm_charge || 0}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {`$${(
                          webhookPerCredit *
                          item?.metadata?.charging?.call_charge
                        ).toFixed(3)}`}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {`$${(item?.metadata?.charging?.llm_price || 0).toFixed(
                          3
                        )}`}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {`$${(
                          webhookPerCredit *
                            item?.metadata?.charging?.call_charge +
                          item?.metadata?.charging?.llm_price
                        ).toFixed(3)}`}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Chip
                        // label={conversation?.analysis?.call_successful}
                        // color={getStatusColor(conversation?.analysis?.call_successful)}
                        label={
                          item?.metadata?.termination_reason ===
                          "No user message received for a long period of time - probably a network error occurred."
                            ? "success"
                            : item?.analysis?.call_successful
                        }
                        color={getStatusColor(
                          item?.metadata?.termination_reason ===
                            "No user message received for a long period of time - probably a network error occurred."
                            ? "success"
                            : item?.analysis?.call_successful
                        )}
                        size="small"
                        variant="outlined"
                      />
                      {/* <Chip
                        label={item?.analysis?.call_successful}
                        color={getStatusColor(item?.analysis?.call_successful)}
                        size="small"
                        variant="outlined"
                      /> */}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, py: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "#6b7280",
                        }}
                      >
                        {item.call_summary_title ||
                          item?.analysis?.call_summary_title ||
                          "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={13} sx={{ textAlign: "center", py: 8 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          backgroundColor: "#f3f4f6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: "#d1d5db",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              backgroundColor: "#9ca3af",
                              borderRadius: "50%",
                            }}
                          />
                        </Box>
                      </Box>
                      <Typography variant="h6" sx={{ color: "#374151", mb: 1 }}>
                        No conversations found
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                        Conversations will appear here when available
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={pagination.totalCount || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid #e5e7eb",
            "& .MuiTablePagination-toolbar": {
              paddingLeft: 2,
              paddingRight: 2,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.875rem",
                color: "#6b7280",
              },
            "& .MuiTablePagination-select": {
              fontSize: "0.875rem",
            },
          }}
        />

        {/* Summary Section */}
        {loading
          ? null
          : summary && (
              <Paper
                sx={{
                  mt: 3,
                  p: isMobile ? 2 : 3,
                  maxWidth: isMobile ? "100%" : 500,
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                  boxShadow: "none",
                  backgroundColor: "#ffffff",
                }}
              >
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  sx={{
                    fontWeight: 600,
                    color: "#111827",
                    mb: 2,
                    borderBottom: "1px solid #e5e7eb",
                    pb: 1,
                  }}
                >
                  Summary
                </Typography>

                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      Calls
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#111827" }}
                    >
                      {summary.total_conversation?.toLocaleString() || 0}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      Duration (min)
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#111827" }}
                    >
                      {summary.total_call_duration_secs
                        ? Math.round(summary.total_call_duration_secs / 60)
                        : 0}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      Messages
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#111827" }}
                    >
                      {summary.total_messages?.toLocaleString() || 0}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      Call Credits
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#111827" }}
                    >
                      {summary.totalCharges_call?.toLocaleString() || 0}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      LLM Credits
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#111827" }}
                    >
                      {summary.lmmCredits?.toLocaleString() || 0}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      Call Cost
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#111827" }}
                    >
                      {`$${(
                        webhookPerCredit * summary.totalCharges_call || 0
                      ).toFixed(2)}`}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      LLM Cost
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#111827" }}
                    >
                      {`$${(summary?.totalCharges_llm || 0).toFixed(2)}`}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      Total Credit
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#111827" }}
                    >
                      {(
                        summary.totalCharges_call + summary.lmmCredits
                      ).toLocaleString()}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      Total Cost
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#111827" }}
                    >
                      {`$${(
                        webhookPerCredit * summary.totalCharges_call +
                        summary?.totalCharges_llm
                      ).toFixed(2)}`}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}
      </Box>
      <ConversationDetailSidebar
        open={sidebarOpen}
        onClose={handleSidebarClose}
        conversationId={selectedConversationId}
      />
    </Layout>
  );
}

export default AllConversations;